/* Members management — list, create, edit, deactivate.
   Members are scheme participants & finance applicants, distinct from system Users. */
const { useState: useStateMb, useEffect: useEffectMb, useRef: useRefMb } = React;

function MembersPage({ go }) {
  const [q, setQ] = useStateMb('');
  const [showActive, setShowActive] = useStateMb(true);
  const [editing, setEditing] = useStateMb(null);
  const [creating, setCreating] = useStateMb(false);

  const filtered = QH_DATA.members.filter(m => {
    if (showActive && !m.active) return false;
    if (!q) return true;
    const qq = q.toLowerCase();
    return (m.fullName || '').toLowerCase().includes(qq)
        || String(m.itsNo).includes(qq)
        || (m.cpr || '').includes(qq)
        || (m.mobile1 || '').includes(qq)
        || (m.email1 || '').toLowerCase().includes(qq);
  });

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Members</span></div>
          <h1>Members</h1>
          <div className="subtitle">{QH_DATA.activeMembersTotal ?? QH_DATA.members.filter(m => m.active).length} active members{QH_DATA.membersTotal != null ? ` · ${QH_DATA.membersTotal} total` : ''}</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => window.QH_STORE.reload()} title="Reload data"><I.Refresh/></button>
          <button className="btn" onClick={() => window.safeDownload('/members/export.csv', 'members.csv')}><I.Download/> Export</button>
          {window.can('members','create') && <button className="btn primary" onClick={() => setCreating(true)}><I.Plus/> New member</button>}
        </div>
      </div>
      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <div className="toolbar" style={{borderRadius:'var(--radius) var(--radius) 0 0',border:'1px solid var(--line)',borderBottom:'none'}}>
          <input className="input" style={{height:28,width:280}} placeholder="Search name, ITS, CPR, mobile, email…" value={q} onChange={e => setQ(e.target.value)}/>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--ink-2)'}}>
            <input type="checkbox" checked={showActive} onChange={e => setShowActive(e.target.checked)}/> Active only
          </label>
          <div className="grow"/>
          <span className="text-xs muted">{filtered.length} of {QH_DATA.members.length}</span>
        </div>
        <div className="table-wrap" style={{borderTopLeftRadius:0,borderTopRightRadius:0,marginTop:-12,overflowX:'auto'}}>
          <table className="data">
            <thead><tr><th>ITS</th><th>Name</th><th>CPR</th><th>Mobile</th><th>Email</th><th>Gender</th><th>Nominee 1</th><th>Nominee 2</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.itsNo} className="clickable"
                    onClick={() => {
                      if (!go) return;
                      // Falls back to opening the edit modal when the
                      // role can't access the dedicated profile page.
                      if (window.can('member_profile', 'read')) go('member:' + m.itsNo);
                      else if (window.can('members', 'edit')) setEditing(m);
                    }}
                    title={window.can('member_profile', 'read') ? 'Open profile' : (window.can('members', 'edit') ? 'Edit member' : '')}>
                  <td className="font-mono text-xs">{m.itsNo}</td>
                  <td>{m.fullName}</td>
                  <td className="font-mono text-xs">{m.cpr || '—'}</td>
                  <td className="font-mono text-xs">{m.mobile1 || '—'}</td>
                  <td className="text-xs">{m.email1 || '—'}</td>
                  <td className="text-xs">{m.gender === 'M' ? 'Male' : m.gender === 'F' ? 'Female' : '—'}</td>
                  <td className="text-xs">
                    {m.nominee1Name
                      ? <><div>{m.nominee1Name}</div><div className="font-mono muted" style={{fontSize:11}}>{m.nominee1Mobile || '—'}</div></>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="text-xs">
                    {m.nominee2Name
                      ? <><div>{m.nominee2Name}</div><div className="font-mono muted" style={{fontSize:11}}>{m.nominee2Mobile || '—'}</div></>
                      : <span className="muted">—</span>}
                  </td>
                  <td><span className={`tag ${m.active ? 'pos' : ''}`}><span className="dot"/>{m.active ? 'active' : 'inactive'}</span></td>
                  <td style={{whiteSpace:'nowrap'}}>
                    {window.can('members','edit') && (
                      <button className="icon-btn" title="Edit" onClick={(e) => { e.stopPropagation(); setEditing(m); }}><I.Edit s={12}/></button>
                    )}
                    <I.ChevronRight s={12}/>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No members match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {creating && <MemberModal onClose={() => setCreating(false)}/>}
      {editing && <MemberModal member={editing} onClose={() => setEditing(null)}/>}
    </>
  );
}

function MemberModal({ member, onClose }) {
  const isEdit = !!member;
  const [form, setForm] = useStateMb({
    itsNo: member?.itsNo || '',
    cpr: member?.cpr || '',
    fullName: member?.fullName || '',
    mobile1: member?.mobile1 || '',
    mobile2: member?.mobile2 || '',
    email1: member?.email1 || '',
    email2: member?.email2 || '',
    gender: member?.gender || '',
    nominee1Name: member?.nominee1Name || '',
    nominee1Mobile: member?.nominee1Mobile || '',
    nominee2Name: member?.nominee2Name || '',
    nominee2Mobile: member?.nominee2Mobile || '',
    active: member?.active ?? true,
  });
  const [busy, setBusy] = useStateMb(false);
  const [err, setErr] = useStateMb('');
  const set = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));

  const submit = async () => {
    setErr(''); setBusy(true);
    try {
      if (isEdit && !form.fullName?.trim()) { setErr('Full name is required.'); setBusy(false); return; }
      const body = {
        cpr: form.cpr || null,
        fullName: form.fullName?.trim() || null,
        mobile1: form.mobile1 || null, mobile2: form.mobile2 || null,
        email1: form.email1 || null, email2: form.email2 || null,
        gender: form.gender || null,
        nominee1Name: form.nominee1Name || null, nominee1Mobile: form.nominee1Mobile || null,
        nominee2Name: form.nominee2Name || null, nominee2Mobile: form.nominee2Mobile || null,
        active: form.active,
      };
      if (isEdit) {
        await window.QH_API.put(`/members/${member.itsNo}`, body);
      } else {
        if (!form.itsNo || !form.fullName?.trim()) { setErr('ITS No. and Full name are required.'); setBusy(false); return; }
        await window.QH_API.post('/members', { itsNo: Number(form.itsNo), ...body });
      }
      await window.QH_STORE.reload();
      onClose();
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete or deactivate member ${member.itsNo}?`)) return;
    setBusy(true);
    try {
      await window.QH_API.delete(`/members/${member.itsNo}`);
      await window.QH_STORE.reload();
      onClose();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{isEdit ? `Edit member ${member.itsNo}` : 'New member'}</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <div className="modal-body" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="field"><label>ITS No.</label><input className="input font-mono" value={form.itsNo} onChange={set('itsNo')} disabled={isEdit}/></div>
          <div className="field"><label>CPR</label><input className="input font-mono" value={form.cpr} onChange={set('cpr')}/></div>
          <div className="field" style={{gridColumn:'1 / -1'}}><label>Full name *</label><input className="input" value={form.fullName} onChange={set('fullName')} placeholder="e.g. Khalid Al Doseri" autoFocus={!isEdit}/></div>
          <div className="field"><label>Mobile 1</label><input className="input font-mono" value={form.mobile1} onChange={set('mobile1')}/></div>
          <div className="field"><label>Mobile 2</label><input className="input font-mono" value={form.mobile2} onChange={set('mobile2')}/></div>
          <div className="field"><label>Email 1</label><input className="input" value={form.email1} onChange={set('email1')}/></div>
          <div className="field"><label>Email 2</label><input className="input" value={form.email2} onChange={set('email2')}/></div>
          <div className="field"><label>Gender</label>
            <select className="select" value={form.gender} onChange={set('gender')}>
              <option value="">—</option><option value="M">Male</option><option value="F">Female</option>
            </select>
          </div>
          <div style={{gridColumn:'1 / -1',borderTop:'1px solid var(--line)',paddingTop:8,marginTop:4,fontSize:11,textTransform:'uppercase',letterSpacing:'0.05em',color:'var(--ink-3)'}}>Nominees</div>
          <div className="field"><label>Nominee 1 name</label><input className="input" value={form.nominee1Name} onChange={set('nominee1Name')}/></div>
          <div className="field"><label>Nominee 1 mobile</label><input className="input font-mono" value={form.nominee1Mobile} onChange={set('nominee1Mobile')}/></div>
          <div className="field"><label>Nominee 2 name</label><input className="input" value={form.nominee2Name} onChange={set('nominee2Name')}/></div>
          <div className="field"><label>Nominee 2 mobile</label><input className="input font-mono" value={form.nominee2Mobile} onChange={set('nominee2Mobile')}/></div>
          {isEdit && (
            <div className="field"><label>Status</label>
              <select className="select" value={form.active ? '1' : '0'} onChange={e => setForm(f => ({...f, active: e.target.value === '1'}))}>
                <option value="1">Active</option><option value="0">Inactive</option>
              </select>
            </div>
          )}
          {err && <div style={{gridColumn:'1 / -1',color:'var(--neg)',fontSize:12}}>{err}</div>}
        </div>
        <div className="modal-footer">
          {isEdit && window.can('members','delete') && <button className="btn danger" onClick={remove} disabled={busy}><I.X/> Delete / deactivate</button>}
          <div style={{flex:1}}/>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}

// Reusable: typeahead dropdown that resolves to a member object via /members/search.
// Caller gets the picked member (or null) via onChange.
function MemberPicker({ value, onChange, autoFocus, placeholder = 'Search ITS / name / CPR…' }) {
  const [q, setQ] = useStateMb(value?.fullName || '');
  const [hits, setHits] = useStateMb([]);
  const [open, setOpen] = useStateMb(false);
  const [active, setActive] = useStateMb(0);
  const tRef = useRefMb(null);
  const wrapRef = useRefMb(null);

  useEffectMb(() => { setQ(value?.fullName || ''); }, [value?.itsNo]);

  useEffectMb(() => {
    if (!q || q.length < 2) { setHits([]); return; }
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      try {
        const r = await window.QH_API.get('/members/search?q=' + encodeURIComponent(q));
        setHits(r); setActive(0);
      } catch {}
    }, 180);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q]);

  useEffectMb(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const choose = (m) => {
    onChange(m);
    setQ(m.fullName);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || !hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(hits.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); choose(hits[active]); }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{position:'relative'}}>
      <input className="input" autoFocus={autoFocus} value={q} placeholder={placeholder}
             onChange={(e) => { setQ(e.target.value); setOpen(true); if (value && e.target.value !== (value.fullName || '')) onChange(null); }}
             onFocus={() => setOpen(true)} onKeyDown={onKey}/>
      {open && hits.length > 0 && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-elev)',border:'1px solid var(--line)',borderRadius:4,boxShadow:'0 8px 24px rgba(0,0,0,0.08)',zIndex:50,maxHeight:280,overflowY:'auto'}}>
          {hits.map((m, i) => (
            <button key={m.itsNo} type="button" onClick={() => choose(m)}
                    className={`nav-item ${i === active ? 'active' : ''}`}
                    style={{padding:'8px 12px',height:'auto',alignItems:'flex-start',flexDirection:'column',gap:2,width:'100%',textAlign:'left',borderBottom:'1px solid var(--line)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,width:'100%'}}>
                <span className="font-mono text-xs muted">{m.itsNo}</span>
                <span style={{fontSize:13,fontWeight:500}}>{m.fullName}</span>
              </div>
              <div className="text-xs muted">{[m.cpr, m.mobile1, m.email1].filter(Boolean).join(' · ')}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────── Member Profile ─────────
// Read-only deep view: header, lifetime KPIs, per-scheme breakdown,
// recent transactions. Accepts an itsNo via the route (member:<itsNo>).
// Pulls /members/{itsNo} + the member's full tx history once on mount;
// the refresh button bumps `tick` to re-fetch without unmounting.
function MemberProfilePage({ itsNo, go }) {
  const [member, setMember] = useStateMb(null);
  const [txs, setTxs] = useStateMb([]);
  const [loading, setLoading] = useStateMb(true);
  const [err, setErr] = useStateMb('');
  const [tick, setTick] = useStateMb(0);

  useEffectMb(() => {
    let cancel = false;
    setLoading(true); setErr('');
    Promise.all([
      window.QH_API.get(`/members/${itsNo}`),
      window.QH_API.get(`/transactions?memberItsNo=${itsNo}&pageSize=10000`),
    ]).then(([m, txResp]) => {
      if (cancel) return;
      setMember(m);
      setTxs(txResp.items || []);
    }).catch(e => { if (!cancel) setErr(e.message || 'Failed to load member.'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [itsNo, tick]);

  if (loading && !member) return <div className="page-body"><div style={{padding:24,color:'var(--ink-3)'}}>Loading…</div></div>;
  if (err) return <div className="page-body"><div style={{padding:14,color:'var(--neg)'}}>{err}</div></div>;
  if (!member) return null;

  const signed = (t) => (t.type === 'deposit' ? +Number(t.amount) : -Number(t.amount));
  const totalDep = txs.filter(t => t.type === 'deposit').reduce((s, t) => s + Number(t.amount), 0);
  const totalWd = txs.filter(t => t.type === 'withdrawal').reduce((s, t) => s + Number(t.amount), 0);
  const balance = totalDep - totalWd;

  // Per-scheme breakdown — sorted by signed balance desc so the
  // schemes with the most exposure surface first.
  const bySch = {};
  for (const t of txs) {
    const k = t.schemeId;
    if (!bySch[k]) bySch[k] = { txs: [], dep: 0, wd: 0 };
    bySch[k].txs.push(t);
    if (t.type === 'deposit') bySch[k].dep += Number(t.amount);
    else bySch[k].wd += Number(t.amount);
  }
  const schemeRows = Object.entries(bySch).map(([sid, d]) => {
    const sch = (window.QH_DATA?.schemes || []).find(x => x.id === sid);
    const dates = d.txs.map(t => (t.date || '').slice(0, 10)).sort();
    return {
      id: sid,
      name: sch?.name || sid,
      code: sch?.code || '',
      dep: d.dep,
      wd: d.wd,
      bal: d.dep - d.wd,
      count: d.txs.length,
      first: dates[0],
      last: dates[dates.length - 1],
    };
  }).sort((a, b) => b.bal - a.bal);

  const recentTxs = [...txs].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 20);
  const eventName = (id) => (window.QH_DATA?.events || []).find(e => e.id === id)?.name || (id || '—');

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <span className="clickable" onClick={() => go('members')} style={{cursor:'pointer'}}>Members</span>
            <I.ChevronRight s={10}/><span>{member.fullName}</span>
          </div>
          <h1>{member.fullName} {!member.active && <span className="tag" style={{marginLeft:8,verticalAlign:'middle',fontSize:11}}>INACTIVE</span>}</h1>
          <div className="subtitle" style={{display:'flex',flexWrap:'wrap',gap:'4px 16px'}}>
            <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>ITS </span><span className="font-mono">{member.itsNo}</span></span>
            {member.cpr && <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>CPR </span><span className="font-mono">{member.cpr}</span></span>}
            {member.mobile1 && <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>Mobile </span><span className="font-mono">{member.mobile1}</span></span>}
            {member.email1 && <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>Email </span>{member.email1}</span>}
            {member.gender && <span><span className="muted text-xs uppercase" style={{letterSpacing:0.4}}>Gender </span>{member.gender === 'M' ? 'Male' : member.gender === 'F' ? 'Female' : member.gender}</span>}
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => go('members')}><I.ArrowRight s={11} style={{transform:'rotate(180deg)'}}/> Back to members</button>
          <button className="btn" onClick={() => setTick(t => t + 1)} title="Reload data"><I.Refresh/></button>
          {window.can('member_statement','read') && (
            <button className="btn" onClick={() => go('memberStatement:' + member.itsNo)}><I.ScrollText s={12}/> Open statement</button>
          )}
        </div>
      </div>

      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <KPIStrip items={[
          { label: 'Current balance', value: fmtBHD(balance) + ' BHD', sub: 'across all schemes' },
          { label: 'Lifetime deposits', value: '+' + fmtBHD(totalDep) + ' BHD', sub: txs.filter(t => t.type === 'deposit').length + ' entries' },
          { label: 'Lifetime withdrawals', value: '−' + fmtBHD(totalWd) + ' BHD', sub: txs.filter(t => t.type === 'withdrawal').length + ' entries' },
          { label: 'Schemes touched', value: String(schemeRows.length) },
          { label: 'Total transactions', value: String(txs.length) },
        ]}/>

        {(member.nominee1Name || member.nominee2Name) && (
          <div className="card">
            <div className="card-header">Nominees</div>
            <div className="card-body" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>
                <div className="text-xs uppercase muted" style={{marginBottom:4}}>Nominee 1</div>
                <div style={{fontWeight:500}}>{member.nominee1Name || '—'}</div>
                {member.nominee1Mobile && <div className="font-mono text-xs muted">{member.nominee1Mobile}</div>}
              </div>
              <div>
                <div className="text-xs uppercase muted" style={{marginBottom:4}}>Nominee 2</div>
                <div style={{fontWeight:500}}>{member.nominee2Name || '—'}</div>
                {member.nominee2Mobile && <div className="font-mono text-xs muted">{member.nominee2Mobile}</div>}
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            Per-scheme breakdown
            <span className="actions"><span className="muted text-xs">click a row to drill into the statement for that scheme</span></span>
          </div>
          <div className="table-wrap" style={{border:'none',borderRadius:0}}>
            <table className="data">
              <thead>
                <tr>
                  <th>Scheme</th>
                  <th className="right">Transactions</th>
                  <th className="right">Deposits</th>
                  <th className="right">Withdrawals</th>
                  <th className="right">Balance · BHD</th>
                  <th>First</th>
                  <th>Last</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {schemeRows.map(r => (
                  <tr key={r.id} className="clickable"
                      onClick={() => window.can('member_statement','read') && go('memberStatement:' + member.itsNo + ':' + r.id)}
                      title={window.can('member_statement','read') ? 'Open statement' : 'You don\'t have access to Member Statement'}>
                    <td>
                      <div style={{fontWeight:500}}>{r.name}</div>
                      <div className="text-xs muted font-mono">{r.code}</div>
                    </td>
                    <td className="num right">{r.count}</td>
                    <td className="num right pos">+{fmtBHD(r.dep)}</td>
                    <td className="num right neg">−{fmtBHD(r.wd)}</td>
                    <td className="num right" style={{fontWeight:600}}>{fmtBHD(r.bal)}</td>
                    <td className="font-mono text-xs">{r.first || '—'}</td>
                    <td className="font-mono text-xs">{r.last || '—'}</td>
                    <td><I.ChevronRight s={12}/></td>
                  </tr>
                ))}
                {schemeRows.length === 0 && (
                  <tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>This member hasn't transacted in any scheme yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Recent transactions <span className="actions"><span className="muted text-xs">last 20 across all schemes</span></span></div>
          <div className="table-wrap" style={{border:'none',borderRadius:0,maxHeight:480,overflow:'auto'}}>
            <table className="data">
              <thead style={{position:'sticky',top:0,background:'var(--bg-elev)',zIndex:1}}>
                <tr>
                  <th>Date</th>
                  <th>Scheme</th>
                  <th>Event</th>
                  <th>Type</th>
                  <th className="right">Amount · BHD</th>
                  <th>Bank</th>
                  <th>Maturity</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {recentTxs.map(t => {
                  const sch = (window.QH_DATA?.schemes || []).find(x => x.id === t.schemeId);
                  return (
                    <tr key={t.id}>
                      <td className="font-mono text-xs">{(t.date || '').slice(0, 10)}</td>
                      <td className="text-xs">{sch?.code || t.schemeId}</td>
                      <td className="text-xs">{eventName(t.eventId)}</td>
                      <td>
                        {t.type === 'deposit'
                          ? <span className="tag pos" style={{textTransform:'none'}}><I.ArrowDown s={10}/> in</span>
                          : <span className="tag neg" style={{textTransform:'none'}}><I.ArrowUp s={10}/> out</span>}
                      </td>
                      <td className={`num right ${t.type === 'deposit' ? 'pos' : 'neg'}`} style={{fontWeight:600}}>
                        {t.type === 'deposit' ? '+' : '−'}{fmtBHD(t.amount)}
                      </td>
                      <td className="text-xs">{t.bank || '—'}</td>
                      <td className="font-mono text-xs muted">{t.maturity || '—'}</td>
                      <td className="text-xs muted">{t.note || '—'}</td>
                    </tr>
                  );
                })}
                {recentTxs.length === 0 && (
                  <tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No transactions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

window.MembersPage = MembersPage;
window.MemberPicker = MemberPicker;
window.MemberModal = MemberModal;
window.MemberProfilePage = MemberProfilePage;
